import Api from '@/common/api'
import axios from 'axios'
import { getUrl } from '@/common/helper'

/**
 * Two-Factor Authentication API Service
 * Handles all 2FA-related API calls to Zion.Core backend
 */
export default {
  /**
   * Verify TOTP code against a challenge
   * @param {Object} request - { challengeId, code, rememberDevice }
   * @returns {Promise} - Response with accessToken and refreshToken on success
   */
  verifyTotp (request) {
    // Use axios directly to avoid adding Authorization header
    return axios.post(
      getUrl('core', 'auth/2fa/verify-totp'),
      request,
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        withCredentials: true // For trusted device cookie
      }
    )
  },

  /**
   * Setup TOTP for the current user
   * Requires authentication (setup token or access token)
   * @returns {Promise} - Response with otpAuthUri and manualKey
   */
  setupTotp () {
    // Use SetupToken for 2FA setup flow
    const setupToken = localStorage.getItem('2fa_setup_token')
    const token = setupToken || localStorage.getItem('token')
    
    return axios.post(
      getUrl('core', 'auth/2fa/setup'),
      {},
      {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        withCredentials: true
      }
    )
  },

  /**
   * Enable 2FA after successful TOTP verification
   * @param {string} code - 6-digit TOTP code
   * @returns {Promise} - Response with success flag and recovery codes
   */
  enableTwoFactor (code) {
    const setupToken = localStorage.getItem('2fa_setup_token')
    const token = setupToken || localStorage.getItem('token')

    return axios.post(
      getUrl('core', 'auth/2fa/enable'),
      { code },
      {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }
    )
  },

  /**
   * Disable 2FA for the current user
   * @param {string} password - User's password for confirmation
   * @returns {Promise} - Response with success status
   */
  disableTwoFactor (password, totpCode) {
    return Api(true).post(
      getUrl('core', 'auth/2fa/disable'),
      { password, totpCode }
    )
  },

  /**
   * Set preferred 2FA method
   * @param {string} preferredMethod - 'Totp' or 'Email'
   * @returns {Promise} - Response with success status
   */
  setPreference (preferredMethod) {
    return Api(true).post(
      getUrl('core', 'auth/2fa/set-preference'),
      { preferredMethod }
    )
  },

  /**
   * Get 2FA status for the current user
   * @returns {Promise} - Response with enabled status and details
   */
  getStatus () {
    return Api(true).get(
      getUrl('core', 'auth/2fa/status')
    )
  }
}
